import React, { useEffect, useState, useMemo } from "react";
import {
  Row, Col, Card, CardBody, CardTitle, Button, Spinner, Alert, UncontrolledTooltip
} from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { RepositoryFactory, RelatedDataHelper } from "../../../repositories";
import { pb } from "../../../helpers/pocketbase_helper";
import { createModuleLogger } from "../../../utils/logger";
import TableContainer from "../../../components/Common/DataTableContainer";
import PDFViewerModal from "../../../components/Common/PDFViewerModal";
import FinalizarOrdenButton from "../../../components/FinalizarOrdenButton";
import ReturnOrderButton from "../../../components/ReturnOrderButton";
import ViewNotesButton from "../../../components/ViewNotesButton";
import AsignarPlanModal from "../../../components/AsignarPlanModal";
import DesasignarPlanModal from "../../../components/Modals/DesasignarPlanModal";
import TipoTrabajoModal from "../../../components/Modals/TipoTrabajoModal";
import EstadisticasOrdenButton from "../../../components/Estadisticas/EstadisticasOrdenButton";
import EstadisticasOrdenModal from "../../../components/Estadisticas/EstadisticasOrdenModal";
import SolicitarMaterialButton from "../../../components/SolicitarMaterialButton";
import { useAsignacionPlan } from "../../../hooks/useAsignacionPlan";
import { updateTrabajoInterno } from "../../../services/TipoTrabajoService";
import { handleCloneDirect } from "../../../helpers/ordenCloneHelper";

// Crear logger para este módulo
const ordenLogger = createModuleLogger('OrdenProduccion');

const OrdenActivaRefactored = ({ mode = "activas" }) => {
  document.title = mode === "finalizadas" 
    ? "Órdenes Finalizadas | GraphicForce - Printesco"
    : "Órdenes Activas | GraphicForce - Printesco";

  // Inicializar repositorio de órdenes de producción
  const ordenRepository = useMemo(() => 
    RepositoryFactory.getRepository('orden_produccion'), 
  []);

  // Hook para asignación/desasignación de planes
  const {
    loading: asignacionLoading,
    error: asignacionError,
    desasignarPlan,
    clearError: clearAsignacionError
  } = useAsignacionPlan();

  // Estados principales
  const [ordenes, setOrdenes] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [ordenesAsignadas, setOrdenesAsignadas] = useState([]);
  const [totalRecordsAsignadas, setTotalRecordsAsignadas] = useState(0);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAsignadas, setLoadingAsignadas] = useState(false);
  const [error, setError] = useState("");

  // Estados para paginación
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [pageIndexAsignadas, setPageIndexAsignadas] = useState(0);
  const [pageSizeAsignadas, setPageSizeAsignadas] = useState(10);
  
  // Estado para controlar si hay filtros activos
  const [hasActiveFilters, setHasActiveFilters] = useState(false);

  // Estados para modales
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [pdfModalData, setPdfModalData] = useState({
    ordenId: null,
    filename: null
  });
  const [modalAsignarPlan, setModalAsignarPlan] = useState(false);
  const [ordenParaPlan, setOrdenParaPlan] = useState(null);
  const [modalDesasignarPlan, setModalDesasignarPlan] = useState(false);
  const [ordenParaDesasignar, setOrdenParaDesasignar] = useState(null);
  const [modalTipoTrabajo, setModalTipoTrabajo] = useState(false);
  const [ordenParaTipoTrabajo, setOrdenParaTipoTrabajo] = useState(null);
  const [modalEstadisticas, setModalEstadisticas] = useState(false);
  const [ordenParaEstadisticas, setOrdenParaEstadisticas] = useState(null);

  // Estado para alertas del sistema
  const [alert, setAlert] = useState({ show: false, type: '', title: '', message: '' });
  const [modalError, setModalError] = useState('');
  const [generatingPdf, setGeneratingPdf] = useState(false);

  // Función para detectar dispositivos móviles
  const detectMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  // Mapa de clientes para mostrar nombres en lugar de IDs
  const clientesMap = useMemo(() => {
    const map = {};
    clientes.forEach(cliente => {
      map[cliente.id] = cliente.razon_social;
    });
    return map;
  }, [clientes]);

  // Función para cargar órdenes asignadas
  const fetchOrdenesAsignadas = async () => {
    try {
      setLoadingAsignadas(true);
      
      // Obtener todas las tareas con órdenes expandidas
      const tareasResult = await pb.collection('tarea_orden').getList(pageIndexAsignadas + 1, pageSizeAsignadas, {
        expand: 'orden_produccion,plan',
        sort: '-created',
        requestKey: null
      });
      
      // Convertir tareas a órdenes con información del plan y filtrar órdenes finalizadas
      const ordenesConTarea = tareasResult.items.map(tarea => {
        const orden = tarea.expand?.orden_produccion;
        if (orden) {
          // Agregar información del plan a la orden
          orden.tarea = tarea.id;
          orden.planInfo = {
            id: tarea.plan,
            nombre: tarea.expand?.plan?.nombre || `Plan #${tarea.plan}`
          };
          return orden;
        }
        return null;
      }).filter(Boolean).filter(orden => {
        // Filtrar órdenes finalizadas - solo mostrar órdenes activas
        return !orden.finalizada || orden.finalizada === false;
      });
      
      setOrdenesAsignadas(ordenesConTarea);
      setTotalRecordsAsignadas(ordenesConTarea.length);
      
      ordenLogger.debug("Órdenes asignadas cargadas", { 
        count: ordenesConTarea.length 
      });
    } catch (err) {
      ordenLogger.error("Error cargando órdenes asignadas", err);
    } finally {
      setLoadingAsignadas(false);
    }
  };

  // Cargar datos con paginación
  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        setError("");

        // Determinar si debemos cargar todos los datos o solo una página
        const shouldLoadAll = hasActiveFilters;
        const pageSizeToUse = shouldLoadAll ? 9999 : pageSize;
        const pageToRequest = shouldLoadAll ? 1 : pageIndex + 1;

        // Fetch data usando repositorio según modo - deshabilitar auto-cancelación
        // Para órdenes activas, usar el método que filtra del lado del servidor las que tienen tarea
        const resultList = mode === 'finalizadas'
          ? await ordenRepository.getOrdersFinalizadas(pageToRequest, pageSizeToUse, { requestKey: null })
          : await ordenRepository.getOrdersInProductionWithoutTask(pageToRequest, pageSizeToUse, { requestKey: null });

        // Establecer datos directamente sin filtrado client-side
        setOrdenes(resultList.items);
        setTotalRecords(resultList.totalItems);

        // Fetch clientes para el mapa
        const clientesData = await RelatedDataHelper.getClientes();
        setClientes(clientesData);

        ordenLogger.debug("Datos cargados exitosamente", {
          mode,
          ordenesCount: resultList.items.length,
          totalRecords: resultList.totalItems,
          hasActiveFilters,
          loadedAll: shouldLoadAll
        });
      } catch (err) {
        ordenLogger.error("Error cargando datos", err);
        setError("Error cargando las órdenes. Por favor, intente nuevamente.");
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [mode, pageIndex, pageSize, hasActiveFilters, ordenRepository]);

  // Cargar órdenes asignadas cuando cambie la paginación
  useEffect(() => {
    if (mode !== 'finalizadas') { // Solo cargar asignadas para órdenes activas
      fetchOrdenesAsignadas();
    }
  }, [mode, pageIndexAsignadas, pageSizeAsignadas]);

  // Función para recargar órdenes
  const reloadOrdenes = async () => {
    try {
      setLoading(true);
      const resultList = mode === 'finalizadas'
        ? await ordenRepository.getOrdersFinalizadas(pageIndex + 1, pageSize, { requestKey: null })
        : await ordenRepository.getOrdersInProduction(pageIndex + 1, pageSize, { requestKey: null });
      
      // Para órdenes finalizadas, mostrar todas las órdenes (con o sin tarea)
      // Para órdenes activas, mantener el filtro de órdenes sin tarea
      if (mode === 'finalizadas') {
        // Mostrar todas las órdenes finalizadas
        setOrdenes(resultList.items);
        setTotalRecords(resultList.totalItems);
      } else {
        // Obtener IDs de órdenes que ya tienen tarea asignada (solo para órdenes activas)
        const ordenesConTareaIds = new Set();
        try {
          const tareasResult = await pb.collection('tarea_orden').getList(1, 1000, {
            fields: 'orden_produccion',
            requestKey: null
          });
          tareasResult.items.forEach(tarea => {
            ordenesConTareaIds.add(tarea.orden_produccion);
          });
        } catch (err) {
          ordenLogger.error("Error obteniendo IDs de órdenes con tarea", err);
        }
        
        // Filtrar solo las que NO tienen tarea asignada para la tabla principal
        const ordenesSinTarea = resultList.items.filter(orden => !ordenesConTareaIds.has(orden.id));
        setOrdenes(ordenesSinTarea);
        setTotalRecords(resultList.totalItems);
      }
      
      // Recargar también las órdenes asignadas si estamos en modo activas
      if (mode !== 'finalizadas') {
        await fetchOrdenesAsignadas();
      }
      
      ordenLogger.debug("Órdenes recargadas exitosamente");
    } catch (err) {
      ordenLogger.error("Error recargando órdenes", err);
      setError("Error recargando las órdenes.");
    } finally {
      setLoading(false);
    }
  };

  // Función para mostrar alertas simples
  const showSimpleAlert = (type, title, message) => {
    setAlert({ show: true, type, title, message });
    setTimeout(() => {
      setAlert({ show: false, type: '', title: '', message: '' });
    }, 5000);
  };

  // Función para abrir modal de desasignar plan
  const handleDesasignarPlan = (orden) => {
    setOrdenParaDesasignar(orden);
    setModalDesasignarPlan(true);
    clearAsignacionError();
  };

  // Función para confirmar desasignación
  const handleConfirmarDesasignacion = async (ordenId) => {
    try {
      const result = await desasignarPlan(ordenId);
      
      if (result.success) {
        showSimpleAlert('success', 'Éxito', 'Plan desasignado correctamente');
        setModalDesasignarPlan(false);
        setOrdenParaDesasignar(null);
        // Recargar ambas listas
        await reloadOrdenes();
        await fetchOrdenesAsignadas();
      } else {
        showSimpleAlert('danger', 'Error', result.error);
      }
    } catch (error) {
      console.error('Error desasignando plan:', error);
      showSimpleAlert('danger', 'Error', 'Error al desasignar el plan');
    }
  };

  // Handler para clonar orden
  const handleClone = async (orden) => {
    await handleCloneDirect(
      orden,
      pb,
      ordenRepository,
      showSimpleAlert,
      reloadOrdenes,
      ordenLogger
    );
  };

  // Handler para ver PDF
  const handlePdf = (orden) => {
    ordenLogger.debug("Abriendo PDF para orden", { ordenId: orden.id, numero: orden.numero });
    setPdfModalData({
      ordenId: orden.id,
      filename: `orden_${orden.numero}.pdf`
    });
    setIsPdfModalOpen(true);
  };

  // Handler para imprimir PDF externo (sin cliente ni asesor)
  const handlePrintExterna = async (orden) => {
    try {
      const isMobile = detectMobile();
      if (isMobile) {
        ordenLogger.info('Dispositivo móvil detectado - usando descarga directa para PDF externo', { ordenId: orden.id, userAgent: navigator.userAgent });
        setModalError('');
        setGeneratingPdf(true);
        try {
          const { printOrdenProduccionExterna } = await import('../pdf/pdf_print');
          await printOrdenProduccionExterna(orden.id);
          ordenLogger.info('PDF externo generado exitosamente en móvil', { ordenId: orden.id });
          setModalError('PDF externo generado exitosamente');
          setTimeout(() => setModalError(''), 3000);
        } catch (mobileError) {
          ordenLogger.error('Error al generar PDF externo en móvil', { error: mobileError, ordenId: orden.id });
          setModalError('Error al generar el PDF externo en dispositivo móvil');
        } finally {
          setGeneratingPdf(false);
        }
      } else {
        ordenLogger.info('Dispositivo desktop detectado - usando modal PDF externo', { ordenId: orden.id });
        setPdfModalData({ ordenId: orden.id, filename: `orden_produccion_externa_${orden.numero}.pdf` });
        setIsPdfModalOpen(true);
      }
    } catch (error) {
      ordenLogger.error('Error al generar PDF externo', { error, ordenId: orden.id });
      setModalError('Error al generar el PDF externo');
      setGeneratingPdf(false);
    }
  };

  // Handler para asignar plan
  const handleAsignarPlan = (orden) => {
    ordenLogger.debug("Abriendo modal para asignar plan", { ordenId: orden.id, numero: orden.numero });
    setOrdenParaPlan(orden);
    setModalAsignarPlan(true);
  };

  // Handler para cerrar modal de asignar plan
  const handleCloseAsignarPlan = () => {
    setModalAsignarPlan(false);
    setOrdenParaPlan(null);
  };

  // Función para manejar el éxito de la asignación de planes
  const handleAsignarPlanSuccess = (data) => {
    setModalAsignarPlan(false);
    setOrdenParaPlan(null);
    // Recargar las órdenes para reflejar los cambios
    reloadOrdenes();
    // Mostrar mensaje de éxito
    showSimpleAlert('success', 'Plan Asignado', 'El plan se ha asignado exitosamente a la orden.');
  };

  // Handler para editar tipo de trabajo
  const handleEditarTipoTrabajo = (orden) => {
    ordenLogger.debug("Abriendo modal para editar tipo de trabajo", { ordenId: orden.id, numero: orden.numero });
    setOrdenParaTipoTrabajo(orden);
    setModalTipoTrabajo(true);
  };

  // Handler para cerrar modal de tipo de trabajo
  const handleCloseTipoTrabajo = () => {
    setModalTipoTrabajo(false);
    setOrdenParaTipoTrabajo(null);
  };

  // Handler para mostrar estadísticas
  const handleMostrarEstadisticas = (ordenId) => {
    // Buscar la orden en la lista actual
    const orden = ordenes.find(o => o.id === ordenId);
    if (orden) {
      ordenLogger.debug("Abriendo modal de estadísticas", { ordenId: orden.id, numero: orden.numero });
      setOrdenParaEstadisticas(orden);
      setModalEstadisticas(true);
    } else {
      ordenLogger.error("No se encontró la orden para mostrar estadísticas", { ordenId });
    }
  };

  // Handler para cerrar modal de estadísticas
  const handleCloseEstadisticas = () => {
    setModalEstadisticas(false);
    setOrdenParaEstadisticas(null);
  };

  // Función para manejar el guardado del trabajo interno
  const handleTrabajoInternoSave = async (ordenId, trabajoInterno) => {
    try {
      ordenLogger.info("Actualizando trabajo interno", { ordenId, trabajoInterno });
      
      // Usar el servicio para actualizar
      const result = await updateTrabajoInterno(ordenId, trabajoInterno);
      
      if (result.success) {
        // Cerrar modal
        handleCloseTipoTrabajo();
        
        // Recargar las órdenes para reflejar los cambios
        reloadOrdenes();
        
        // Mostrar mensaje de éxito
        const tipoTexto = trabajoInterno ? 'INTERNO' : 'EXTERNO';
        showSimpleAlert('success', 'Trabajo Interno Actualizado', `El trabajo interno se ha cambiado a ${tipoTexto}.`);
        
        ordenLogger.info("Trabajo interno actualizado exitosamente", { 
          ordenId, 
          trabajoInterno, 
          updatedData: result.data 
        });
      } else {
        // Mostrar error del servicio
        showSimpleAlert('danger', 'Error', result.error || 'Error al actualizar el trabajo interno.');
        ordenLogger.error("Error del servicio al actualizar trabajo interno", { 
          error: result.error, 
          ordenId, 
          trabajoInterno 
        });
        throw new Error(result.error);
      }
      
    } catch (error) {
      ordenLogger.error("Error al actualizar trabajo interno", { error, ordenId, trabajoInterno });
      showSimpleAlert('danger', 'Error', 'Error al actualizar el trabajo interno. Por favor, intente nuevamente.');
      throw error; // Re-lanzar el error para que el modal pueda manejarlo
    }
  };

  // Definir las columnas de la tabla simplificada
  const columns = useMemo(() => [
    {
      header: "N° Orden",
      accessorKey: "numero",
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: (row, columnId, filterValue) => {
        // Filtrar números - convertir a string para comparación
        const numeroValue = String(row.getValue(columnId) || "");
        const searchValue = String(filterValue || "");
        const matches = numeroValue.includes(searchValue);
        return matches;
      },
    },
    {
      header: "Cliente",
      accessorKey: "cliente",
      cell: info => clientesMap[info.getValue()] || "-",
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: (row, columnId, filterValue) => {
        // Filtrar por nombre del cliente, no por ID
        const clienteNombre = clientesMap[row.getValue(columnId)] || "";
        const matches = clienteNombre.toLowerCase().includes(filterValue.toLowerCase());
        return matches;
      },
    },
    {
      header: "Producto",
      accessorKey: "nombre_producto",
      cell: info => info.getValue() || "-",
      enableSorting: true,
      enableColumnFilter: true,
    },
    {
      header: "Fecha Inicio",
      accessorKey: "fecha_inicio",
      cell: info => info.getValue() ? info.getValue().substring(0, 10) : "",
      enableSorting: true,
    },
    {
      header: "Tipo de Trabajo",
      accessorKey: "trabajo_interno",
      cell: info => {
        const trabajoInterno = info.getValue();
        return (
          <span className={`badge ${trabajoInterno ? 'bg-success' : 'bg-info'}`}>
            {trabajoInterno ? 'INTERNO' : 'EXTERNO'}
          </span>
        );
      },
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: (row, columnId, filterValue) => {
        const trabajoInterno = row.getValue(columnId);
        const tipoTexto = trabajoInterno ? 'INTERNO' : 'EXTERNO';
        return tipoTexto.toLowerCase().includes(filterValue.toLowerCase());
      },
    },
    {
      header: "Acciones",
      accessorKey: "acciones",
      cell: info => {
        const orden = info.row.original;
        const rowId = `row-${orden.id || Math.random().toString(36).substring(7)}`;
        return (
          <div style={{ display: "flex", flexWrap: "nowrap", justifyContent: "center", gap: 2 }}>
            <Button id={`clone-${rowId}`} color="info" size="sm" className="me-1 px-2 py-1" onClick={() => handleClone(orden)} title="Clonar orden">
              <i className="fas fa-clone"></i>
            </Button>

            <Button id={`pdf-${rowId}`} color="success" size="sm" className="me-1 px-2 py-1" onClick={() => handlePdf(orden)}>
              <i className="fas fa-file-pdf"></i>
            </Button>
            <UncontrolledTooltip placement="top" target={`pdf-${rowId}`}>
              Generar PDF
            </UncontrolledTooltip>

            {/* Botón Solicitar Material - Solo para órdenes activas y órdenes con plan asignado */}
            {mode !== "finalizadas" && (
              <SolicitarMaterialButton
                ordenId={orden.id}
                onMaterialRequested={() => {
                  // Optional: reload data or show notification
                  console.log("Material request completed for order", orden.id);
                }}
              />
            )}

            {/* Botón de imprimir solo para órdenes externas */}
            {orden.trabajo_interno === false && (
              <>
                <Button id={`print-${rowId}`} color="secondary" size="sm" className="me-1 px-2 py-1" onClick={() => handlePrintExterna(orden)}>
                  <i className="fas fa-print"></i>
                </Button>
                <UncontrolledTooltip placement="top" target={`print-${rowId}`}>
                  Imprimir PDF sin cliente ni asesor
                </UncontrolledTooltip>
              </>
            )}

            {/* Botón Editar Tipo de Trabajo - Solo para órdenes activas */}
            {mode !== "finalizadas" && (
              <>
                <Button 
                  id={`edit-tipo-trabajo-${rowId}`} 
                  color="primary" 
                  size="sm" 
                  className="me-1 px-2 py-1" 
                  onClick={() => handleEditarTipoTrabajo(orden)}
                  data-testid={`edit-tipo-trabajo-${orden.id}`}
                >
                  <i className="fas fa-edit"></i>
                </Button>
                <UncontrolledTooltip placement="top" target={`edit-tipo-trabajo-${rowId}`}>
                  Editar Tipo de Trabajo
                </UncontrolledTooltip>
              </>
            )}

            {/* Botón de Estadísticas - Solo para órdenes finalizadas */}
            {mode === "finalizadas" && (
              <EstadisticasOrdenButton
                ordenId={orden.id}
                onOpenModal={handleMostrarEstadisticas}
              />
            )}

            {/* Botón Asignar Plan - Siempre visible, deshabilitado si ya tiene plan o está finalizada */}
            <Button 
              id={`asignar-plan-${rowId}`} 
              color={orden.tarea || orden.finalizada ? "secondary" : "warning"} 
              size="sm" 
              className="me-1 px-2 py-1" 
              onClick={() => handleAsignarPlan(orden)}
              disabled={orden.tarea || orden.finalizada}
            >
              <i className="fas fa-tasks"></i>
            </Button>
            <UncontrolledTooltip placement="top" target={`asignar-plan-${rowId}`}>
              {orden.tarea || orden.finalizada ? "Plan ya asignado" : "Asignar Plan"}
            </UncontrolledTooltip>

            {/* Botón Finalizar - Solo para órdenes activas */}
            {mode !== "finalizadas" && (
              <FinalizarOrdenButton
                orden={orden}
                onFinalizada={reloadOrdenes}
                onShowAlert={showSimpleAlert}
              />
            )}

            <ReturnOrderButton
              orden={orden}
              onReturnOrder={reloadOrdenes}
              onShowAlert={showSimpleAlert}
            />

            <ViewNotesButton
              orden={orden}
            />
          </div>
        );
      },
      enableSorting: false,
      enableColumnFilter: false,
      meta: {
        style: {
          width: "200px",
          maxWidth: "200px",
          minWidth: "200px",
          textAlign: "center",
          whiteSpace: "nowrap",
          overflow: "visible"
        }
      },
    },
  ], [clientesMap]);

  // Definir las columnas para la tabla de órdenes asignadas
  const columnsAsignadas = useMemo(() => [
    {
      header: "N° Orden",
      accessorKey: "numero",
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: (row, columnId, filterValue) => {
        const numeroValue = String(row.getValue(columnId) || "");
        const searchValue = String(filterValue || "");
        const matches = numeroValue.includes(searchValue);
        return matches;
      },
    },
    {
      header: "Cliente",
      accessorKey: "cliente",
      cell: info => clientesMap[info.getValue()] || "-",
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: (row, columnId, filterValue) => {
        const clienteNombre = clientesMap[row.getValue(columnId)] || "";
        const matches = clienteNombre.toLowerCase().includes(filterValue.toLowerCase());
        return matches;
      },
    },
    {
      header: "Producto",
      accessorKey: "nombre_producto",
      cell: info => info.getValue() || "-",
      enableSorting: true,
      enableColumnFilter: true,
    },
    {
      header: "Fecha Inicio",
      accessorKey: "fecha_inicio",
      cell: info => info.getValue() ? info.getValue().substring(0, 10) : "",
      enableSorting: true,
    },
    {
      header: "Plan Asignado",
      accessorKey: "tarea",
      cell: info => {
        const orden = info.row.original;
        // Usar la información del plan que agregamos en fetchOrdenesAsignadas
        if (orden.planInfo && orden.planInfo.nombre) {
          return orden.planInfo.nombre;
        }
        // Fallback al ID si no hay información del plan
        return orden.tarea ? `Plan #${orden.tarea}` : "N/A";
      },
      enableSorting: true,
    },
    {
      header: "Acciones",
      accessorKey: "acciones",
      cell: info => {
        const orden = info.row.original;
        const rowId = `asignada-row-${orden.id || Math.random().toString(36).substring(7)}`;
        return (
          <div style={{ display: "flex", flexWrap: "nowrap", justifyContent: "center", gap: 2 }}>
            <Button id={`pdf-asignada-${rowId}`} color="success" size="sm" className="me-1 px-2 py-1" onClick={() => handlePdf(orden)}>
              <i className="fas fa-file-pdf"></i>
            </Button>
            <UncontrolledTooltip placement="top" target={`pdf-asignada-${rowId}`}>
              Generar PDF
            </UncontrolledTooltip>

            {/* Botón Solicitar Material - Para órdenes con plan asignado */}
            <SolicitarMaterialButton
              ordenId={orden.id}
              onMaterialRequested={() => {
                // Optional: reload data or show notification
                console.log("Material request completed for assigned order", orden.id);
              }}
            />

            {/* Botón de imprimir solo para órdenes externas */}
            {orden.trabajo_interno === false && (
              <>
                <Button id={`print-asignada-${rowId}`} color="secondary" size="sm" className="me-1 px-2 py-1" onClick={() => handlePrintExterna(orden)}>
                  <i className="fas fa-print"></i>
                </Button>
                <UncontrolledTooltip placement="top" target={`print-asignada-${rowId}`}>
                  Imprimir PDF sin cliente ni asesor
                </UncontrolledTooltip>
              </>
            )}

            {/* Botón Desasignar Plan - Solo visible si tiene plan asignado */}
            {orden.tarea && (
              <>
                <Button 
                  id={`desasignar-plan-${rowId}`} 
                  color="danger" 
                  size="sm" 
                  className="me-1 px-2 py-1" 
                  onClick={() => handleDesasignarPlan(orden)}
                  disabled={asignacionLoading}
                >
                  <i className="fas fa-times"></i>
                </Button>
                <UncontrolledTooltip placement="top" target={`desasignar-plan-${rowId}`}>
                  Desasignar Plan
                </UncontrolledTooltip>
              </>
            )}

            {/* Botón Retornar Orden - Solo para órdenes activas sin plan asignado */}
            {mode !== "finalizadas" && (
              <ReturnOrderButton
                orden={orden}
                onReturnOrder={reloadOrdenes}
                onShowAlert={showSimpleAlert}
              />
            )}

            {/* Botón Finalizar - Solo para órdenes activas */}
            {mode !== "finalizadas" && (
              <FinalizarOrdenButton
                orden={orden}
                onFinalizada={reloadOrdenes}
                onShowAlert={showSimpleAlert}
              />
            )}

            <ViewNotesButton
              orden={orden}
            />
          </div>
        );
      },
      enableSorting: false,
      enableColumnFilter: false,
      meta: {
        style: {
          width: "150px",
          maxWidth: "150px",
          minWidth: "150px",
          textAlign: "center",
          whiteSpace: "nowrap",
          overflow: "visible"
        }
      },
    },
  ], [clientesMap]);

  // Callbacks para manejo de filtros
  const handleFilterChange = ({ hasFilters }) => {
    setHasActiveFilters(hasFilters);
    
    // Si se activan filtros, resetear a página 1
    if (hasFilters && pageIndex !== 0) {
      setPageIndex(0);
    }
  };

  const handleClearFilters = () => {
    setHasActiveFilters(false);
    if (pageIndex !== 0) {
      setPageIndex(0);
    }
  };

  // Render
  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs 
            maintitle="Dashboard" 
            title="Producción" 
            breadcrumbItem={mode === "finalizadas" ? "Órdenes Finalizadas" : "Órdenes Activas"} 
          />

          {/* Alerta del sistema */}
          {alert.show && (
            <Alert
              color={alert.type}
              isOpen={alert.show}
              toggle={() => setAlert({ show: false, type: '', title: '', message: '' })}
              fade={true}
              transition={{ timeout: 150 }}
              className="d-flex align-items-center justify-content-between mb-3"
            >
              <div>
                <strong>{alert.title}</strong> {alert.message}
              </div>
            </Alert>
          )}

          {/* Alerta de error */}
          {error && (
            <Alert
              color="danger"
              isOpen={!!error}
              toggle={() => setError("")}
              fade={true}
              transition={{ timeout: 150 }}
              className="d-flex align-items-center justify-content-between mb-3"
            >
              <div>
                <strong>Error:</strong> {error}
              </div>
            </Alert>
          )}

          <Row>
            <Col>
              <Card>
                <CardBody>
                  <CardTitle className="h4 d-flex justify-content-between align-items-center">
                    {mode === "finalizadas" ? "Órdenes Finalizadas" : "Órdenes Sin Asignar"}
                    <div>
                      <Button
                        color="secondary"
                        size="sm"
                        title="Recargar tabla"
                        onClick={reloadOrdenes}
                        disabled={loading}
                      >
                        {loading ? <Spinner size="sm" /> : <i className="fas fa-sync-alt"></i>}
                      </Button>
                    </div>
                  </CardTitle>
                  
                  <TableContainer
                    columns={columns}
                    data={ordenes}
                    isGlobalFilter={false}
                    isPagination={true}
                    isCustomPageSize={true}
                    pageIndex={pageIndex}
                    setPageIndex={setPageIndex}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    totalRecords={totalRecords}
                    pagination="pagination"
                    paginationWrapper='dataTables_paginate paging_simple_numbers'
                    tableClass="table table-bordered table-nowrap dt-responsive nowrap w-100"
                    onFilterChange={handleFilterChange}
                    onClearFilters={handleClearFilters}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Tabla de Órdenes Asignadas - Solo para modo activas */}
          {mode !== "finalizadas" && (
            <Row className="mt-4">
              <Col>
                <Card>
                  <CardBody>
                    <CardTitle className="h4 d-flex justify-content-between align-items-center">
                      Órdenes Con Plan Asignado
                      <div>
                        <Button
                          color="secondary"
                          size="sm"
                          title="Recargar tabla"
                          onClick={fetchOrdenesAsignadas}
                          disabled={loadingAsignadas}
                        >
                          {loadingAsignadas ? <Spinner size="sm" /> : <i className="fas fa-sync-alt"></i>}
                        </Button>
                      </div>
                    </CardTitle>
                    
                    <TableContainer
                      columns={columnsAsignadas}
                      data={ordenesAsignadas}
                      isGlobalFilter={false}
                      isPagination={true}
                      isCustomPageSize={true}
                      pageIndex={pageIndexAsignadas}
                      setPageIndex={setPageIndexAsignadas}
                      pageSize={pageSizeAsignadas}
                      setPageSize={setPageSizeAsignadas}
                      totalRecords={totalRecordsAsignadas}
                      pagination="pagination"
                      paginationWrapper='dataTables_paginate paging_simple_numbers'
                      tableClass="table table-bordered table-nowrap dt-responsive nowrap w-100"
                    />
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </div>
      </div>

      {/* Modal PDF Viewer */}
      <PDFViewerModal
        isOpen={isPdfModalOpen}
        toggle={() => setIsPdfModalOpen(false)}
        ordenId={pdfModalData.ordenId}
        filename={pdfModalData.filename}
        titulo="PDF de Orden de Producción"
      />

      {/* Modal Asignar Plan */}
      <AsignarPlanModal
        isOpen={modalAsignarPlan}
        toggle={handleCloseAsignarPlan}
        ordenId={ordenParaPlan?.id || ''}
        ordenNombre={ordenParaPlan?.nombre_producto || `Orden ${ordenParaPlan?.numero || ''}`}
        onSuccess={handleAsignarPlanSuccess}
      />

      {/* Modal Desasignar Plan */}
      <DesasignarPlanModal
        isOpen={modalDesasignarPlan}
        toggle={() => setModalDesasignarPlan(false)}
        ordenId={ordenParaDesasignar?.id || ''}
        ordenNombre={ordenParaDesasignar?.nombre_producto || `Orden ${ordenParaDesasignar?.numero || ''}`}
        planNombre={ordenParaDesasignar?.planInfo?.nombre || 'Plan asignado'}
        onConfirm={handleConfirmarDesasignacion}
        loading={asignacionLoading}
        error={asignacionError}
      />

      {/* Modal Editar Tipo de Trabajo */}
      <TipoTrabajoModal
        isOpen={modalTipoTrabajo}
        toggle={handleCloseTipoTrabajo}
        ordenId={ordenParaTipoTrabajo?.id || ''}
        currentValue={ordenParaTipoTrabajo?.trabajo_interno ?? null}
        onSave={handleTrabajoInternoSave}
      />

      {/* Modal de Estadísticas - Solo para órdenes finalizadas */}
      {modalEstadisticas && ordenParaEstadisticas && (
        <EstadisticasOrdenModal
          isOpen={modalEstadisticas}
          onClose={handleCloseEstadisticas}
          ordenId={ordenParaEstadisticas.id}
          ordenData={ordenParaEstadisticas}
        />
      )}
    </React.Fragment>
  );
};

export default OrdenActivaRefactored;
